// PWAInstallBanner.jsx — subtle bottom banner inviting mobile users to add
// the Bhrigu app to their home screen. Same pattern as MotoNadi.
//
// Behavior:
//   • Mounts globally (App.js root) → can appear on any page
//   • 5-second delay after page-load before first appearance
//   • Mobile-only (UA + viewport width check)
//   • Hidden when already installed (display-mode: standalone | iOS standalone)
//   • Hidden permanently after a single "Not now" tap (localStorage)
//   • Android Chrome: stores `beforeinstallprompt` event and fires it on click
//   • iOS Safari: no native prompt exists — show share-menu instructions instead
//
// July 2026

import { useEffect, useState } from "react";
import { X } from "lucide-react";

const DISMISS_KEY = "bhrigu_pwa_dismissed";
const SHOW_DELAY_MS = 5000;

const isMobile = () => {
  if (typeof navigator === "undefined") return false;
  const ua = (navigator.userAgent || "").toLowerCase();
  const uaMobile = /android|iphone|ipad|ipod|mobile|blackberry|iemobile|opera mini/.test(ua);
  // Belt + suspenders — also check screen width so DevTools "Toggle device"
  // testing works and tablets in landscape fall through correctly.
  const narrow = typeof window !== "undefined" && window.innerWidth < 820;
  return uaMobile || narrow;
};

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  // Android / desktop PWA
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS-specific flag
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
};

export default function PWAInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    // Eligibility checks — bail early if any fail.
    if (typeof window === "undefined") return;
    if (!isMobile()) return;
    if (isStandalone()) return;
    try {
      if (window.localStorage?.getItem(DISMISS_KEY) === "1") return;
    } catch { /* private mode — fall through and let session-only dismiss work */ }

    const ios = isIOS();
    setIosMode(ios);

    // Android Chrome: capture the install event and surface our own UI.
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // If the app gets installed (Android), hide the banner immediately.
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);

    // Reveal after 5s. iOS users see the banner even without the
    // beforeinstallprompt event (it's purely informational on iOS).
    // Android users see it only if Chrome reports the app is installable;
    // if `installEvent` is still null when the timer fires, we still show
    // it — Chrome occasionally delays the event past 5s on slow networks.
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (installEvent) {
      // Android path — fire the captured prompt.
      try {
        installEvent.prompt();
        const choice = await installEvent.userChoice;
        if (choice?.outcome === "accepted") {
          setVisible(false);
        }
      } catch {
        // Prompt can only be called once. If anything goes wrong, just hide.
        setVisible(false);
      }
      setInstallEvent(null);
    } else if (iosMode) {
      // iOS path — there's no prompt API, the banner already shows the
      // instructions inline. Tapping "Add to Home Screen" just acknowledges.
      setVisible(false);
    } else {
      // Android but event not (yet) captured — let user try again later.
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    try { window.localStorage?.setItem(DISMISS_KEY, "1"); } catch { /* private mode */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-3 right-3 bottom-3 z-[9999] mx-auto max-w-md"
      style={{
        background: "var(--cream-soft, #FBEFD4)",
        border: "1px solid rgba(201, 150, 43, 0.4)",
        borderLeft: "3px solid #E8562B",
        boxShadow: "0 6px 24px rgba(46, 26, 14, 0.16)",
        animation: "pwa-banner-rise 380ms cubic-bezier(0.25, 1, 0.4, 1) both",
      }}
      role="dialog"
      aria-label="Install Bhrigu app"
      data-testid="pwa-install-banner"
    >
      <style>{`
        @keyframes pwa-banner-rise {
          from { opacity: 0; transform: translate3d(0, 16px, 0); }
          to   { opacity: 1; transform: translate3d(0, 0, 0); }
        }
      `}</style>

      <div className="px-4 py-3.5 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div
            className="text-[13px] leading-snug"
            style={{ color: "#2E1A0E", fontFamily: "'Fraunces', Georgia, serif" }}
          >
            Add <strong>Bhrigu</strong> to your home screen for the best Vakya Voice experience.
          </div>

          {iosMode && (
            <div
              className="mt-1.5 text-[11px] leading-snug"
              style={{ color: "#8A6E56", fontFamily: "'JetBrains Mono', monospace" }}
            >
              Tap <span style={{ color: "#E8562B" }}>Share</span> then
              {" "}<span style={{ color: "#E8562B" }}>Add to Home Screen</span>.
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleInstall}
              className="text-[10px] tracking-[0.28em] uppercase px-3.5 py-2 transition-all"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                background: "#E8562B",
                color: "#FBEFD4",
                border: "1px solid #C73E17",
                cursor: "pointer",
              }}
              data-testid="pwa-install-btn"
            >
              {iosMode ? "Got it" : "Add to Home Screen"}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="text-[10px] tracking-[0.28em] uppercase px-3.5 py-2 transition-colors"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                color: "#8A6E56",
                background: "transparent",
                border: "1px solid rgba(201, 150, 43, 0.4)",
                cursor: "pointer",
              }}
              data-testid="pwa-dismiss-btn"
            >
              Not now
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Close"
          className="-mr-1 -mt-1 p-1.5 transition-opacity hover:opacity-60"
          style={{ color: "#8A6E56", background: "transparent", border: "none", cursor: "pointer" }}
          data-testid="pwa-close-btn"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
